import type { NextRouter } from 'next/router';
import { useCallback, useMemo, useState } from 'react';
import type { IntlShape } from 'react-intl';

import type { OrderDetailsOrderLine } from '@/types/orderDetailsOrder';

import type { SelectOption } from '@/components/Select';
import type { OrderAppStorefrontWithdrawalEligibilityQuery } from '@/graphql/orderApp/api';
import { WITHDRAWAL_ENABLED } from '@/lib/const';
import { captureError } from '@/lib/errors';
import type { paths } from '@/lib/paths';
import { isEligibleLine } from '@/lib/withdrawal/eligibility';
import type { WithdrawalEligibility } from '@/lib/withdrawal/types';
import { isWithdrawalExcludedOrderLine } from '@/lib/withdrawal/withdrawalExcludedProductTypes';
import * as PM from '@/messages/payment';
import { buildWithdrawalSubmitItems } from '@/views/OrderReturnView/orderReturnSubmitItems';
import type { LineReturnFields } from '@/views/OrderReturnView/orderReturnTypes';
import {
  submitGuestWithdrawal,
  submitRegisteredWithdrawal,
} from '@/views/OrderReturnView/orderReturnWithdrawalSubmit';

export type OrderReturnRouter = {
  paths: typeof paths;
  push: NextRouter['push'];
};

export type GuestContext = {
  authContext?: {
    orderEmail: string;
    orderNumber: string;
    recaptchaToken: string;
  } | null;
  onCancel?: () => void;
  onEligibilityChange?: (eligibility: WithdrawalEligibility | null) => void;
};

export type WithdrawalContext = {
  eligibilityData: OrderAppStorefrontWithdrawalEligibilityQuery | undefined;
  eligibilityOverride?: WithdrawalEligibility | null;
  isEligibilityLoading: boolean;
  refetchEligibility: () => Promise<unknown>;
};

export type UseOrderReturnStateParams = {
  formatMessage: IntlShape['formatMessage'];
  guest: GuestContext;
  isGuestMode: boolean;
  orderAppOrderId: string | undefined;
  orderId: string;
  reasonOptions: SelectOption[];
  returnableLines: OrderDetailsOrderLine[];
  router: OrderReturnRouter;
  withdrawal: WithdrawalContext;
};

export function useOrderReturnState({
  formatMessage,
  guest,
  isGuestMode,
  orderAppOrderId,
  orderId,
  reasonOptions,
  returnableLines,
  router,
  withdrawal,
}: UseOrderReturnStateParams) {
  const { paths, push } = router;
  const {
    authContext: guestAuthContext,
    onCancel: onGuestCancel,
    onEligibilityChange: onGuestEligibilityChange,
  } = guest;
  const {
    eligibilityData: withdrawalEligibilityData,
    eligibilityOverride: withdrawalEligibilityOverride,
    isEligibilityLoading: isWithdrawalEligibilityLoading,
    refetchEligibility: refetchWithdrawalEligibility,
  } = withdrawal;

  const [selectedLineIds, setSelectedLineIds] = useState<
    Record<string, boolean>
  >({});
  const [lineReturnFields, setLineReturnFields] = useState<
    Record<string, LineReturnFields>
  >({});

  const [submitErrorMessage, setSubmitErrorMessage] = useState<string | null>(
    null,
  );
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isReturnConfirmOpen, setIsReturnConfirmOpen] = useState(false);
  const [submitInFlight, setSubmitInFlight] = useState(false);
  const [submitRecaptchaKey, setSubmitRecaptchaKey] = useState(Date.now());
  const [submitRecaptchaToken, setSubmitRecaptchaToken] = useState<
    string | null
  >(null);

  const eligibilityLinesByOrderLineId = useMemo(() => {
    const lines = isGuestMode
      ? (withdrawalEligibilityOverride?.lines ?? [])
      : (withdrawalEligibilityData?.withdrawalEligibility.withdrawal?.lines ??
        []);
    return new Map(lines.map((l) => [l.orderLineId, l]));
  }, [isGuestMode, withdrawalEligibilityData, withdrawalEligibilityOverride]);

  const selectedCount = useMemo(
    () => Object.values(selectedLineIds).filter(Boolean).length,
    [selectedLineIds],
  );

  const getLineEligibilityState = useCallback(
    (lineId: string) => {
      const eligibilityLine = eligibilityLinesByOrderLineId.get(lineId);
      const isKnown = Boolean(eligibilityLine);
      const normalizedEligibilityLine = eligibilityLine
        ? {
            orderLineId: eligibilityLine.orderLineId,
            quantity: eligibilityLine.quantity,
            eligibleQuantity: eligibilityLine.eligibleQuantity,
            status: eligibilityLine.status,
            disabledReasonCode: eligibilityLine.disabledReasonCode ?? null,
            windowEndAt: eligibilityLine.windowEndAt ?? null,
          }
        : null;
      const isEligible = normalizedEligibilityLine
        ? isEligibleLine(normalizedEligibilityLine)
        : false;
      return { eligibilityLine, isKnown, isEligible };
    },
    [eligibilityLinesByOrderLineId],
  );

  const getSelectedLineIdsList = useCallback(
    () =>
      Object.entries(selectedLineIds)
        .filter(([, isSelected]) => Boolean(isSelected))
        .map(([lineId]) => lineId),
    [selectedLineIds],
  );

  const returnableLinesById = useMemo(
    () => new Map(returnableLines.map((line) => [line.id, line])),
    [returnableLines],
  );

  const findInvalidSelectedLine = useCallback(
    (selectedIds: string[]) =>
      selectedIds.find((lineId) => {
        const orderLine = returnableLinesById.get(lineId);
        if (orderLine && isWithdrawalExcludedOrderLine(orderLine)) {
          return true;
        }
        return !getLineEligibilityState(lineId).isEligible;
      }),
    [getLineEligibilityState, returnableLinesById],
  );

  const setLineSelected = useCallback((lineId: string, checked: boolean) => {
    setSelectedLineIds((prev) => ({ ...prev, [lineId]: checked }));
    if (checked) {
      setLineReturnFields((prev) => ({
        ...prev,
        [lineId]: prev[lineId] ?? { description: '', reason: '' },
      }));
    } else {
      setLineReturnFields((prev) => {
        const next = { ...prev };
        delete next[lineId];
        return next;
      });
    }
  }, []);

  const updateLineField = useCallback(
    (lineId: string, patch: Partial<LineReturnFields>) => {
      setLineReturnFields((prev) => ({
        ...prev,
        [lineId]: {
          ...(prev[lineId] ?? { description: '', reason: '' }),
          ...patch,
        },
      }));
    },
    [],
  );

  const goBackToOrderDetails = useCallback(() => {
    setIsReturnConfirmOpen(false);
    if (isGuestMode && onGuestCancel) {
      onGuestCancel();
      return;
    }

    push(
      paths.myAccount.myOrders.orderDetails.url({
        orderId,
        query: {
          type: 'ORDERHISTORY',
        },
      }),
    );
  }, [isGuestMode, onGuestCancel, orderId, paths.myAccount.myOrders, push]);

  const handleRequestSubmit = useCallback(() => {
    if ((!isGuestMode && !orderAppOrderId) || isSubmitted) return;

    setSubmitErrorMessage(null);

    const selectedLineIdsList = getSelectedLineIdsList();
    const invalidSelected = findInvalidSelectedLine(selectedLineIdsList);

    if (invalidSelected) {
      setSubmitErrorMessage(
        formatMessage(PM.paymentMessages.withdrawalSelectionIneligible),
      );
      return;
    }

    setIsReturnConfirmOpen(true);
  }, [
    findInvalidSelectedLine,
    formatMessage,
    getSelectedLineIdsList,
    isGuestMode,
    isSubmitted,
    orderAppOrderId,
  ]);

  const performReturnSubmit = useCallback(async () => {
    if (
      (!isGuestMode && !orderAppOrderId) ||
      isSubmitted ||
      !WITHDRAWAL_ENABLED
    )
      return;

    setSubmitErrorMessage(null);

    const selectedLineIdsList = getSelectedLineIdsList();
    const invalidSelected = findInvalidSelectedLine(selectedLineIdsList);

    if (invalidSelected) {
      setSubmitErrorMessage(
        formatMessage(PM.paymentMessages.withdrawalSelectionIneligible),
      );
      setIsReturnConfirmOpen(false);
      return;
    }

    const quantityByLineId = Object.fromEntries(
      selectedLineIdsList.map((lineId) => {
        const line = eligibilityLinesByOrderLineId.get(lineId);
        const eligibleQty = line?.eligibleQuantity;
        const quantity =
          typeof eligibleQty === 'number' && eligibleQty > 0 ? eligibleQty : 1;
        return [lineId, quantity];
      }),
    );

    const items = buildWithdrawalSubmitItems(
      selectedLineIdsList,
      lineReturnFields,
      reasonOptions,
      quantityByLineId,
    );

    setSubmitInFlight(true);
    try {
      const outcome = isGuestMode
        ? await submitGuestWithdrawal({
            formatMessage,
            guestAuthContext,
            items,
            orderId,
            submitRecaptchaToken,
          })
        : await submitRegisteredWithdrawal({
            formatMessage,
            items,
            orderAppOrderId: orderAppOrderId!,
          });

      if (outcome.status === 'submitted') {
        setIsReturnConfirmOpen(false);
        if (isGuestMode) {
          onGuestEligibilityChange?.({
            code: 'DUPLICATE_REQUEST',
            lines: [],
          });
        } else {
          await refetchWithdrawalEligibility();
        }
        setIsSubmitted(true);
        return;
      }

      setSubmitErrorMessage(outcome.message);
      setIsReturnConfirmOpen(false);
    } catch (error) {
      captureError(error as Error, {
        extra: { route: 'OrderReturnView.performReturnSubmit' },
      });
      setSubmitErrorMessage(
        formatMessage(PM.paymentMessages.withdrawalGenericError),
      );
      setIsReturnConfirmOpen(false);
    } finally {
      setSubmitInFlight(false);
    }
  }, [
    eligibilityLinesByOrderLineId,
    findInvalidSelectedLine,
    formatMessage,
    getSelectedLineIdsList,
    guestAuthContext,
    isGuestMode,
    isSubmitted,
    lineReturnFields,
    onGuestEligibilityChange,
    orderAppOrderId,
    orderId,
    reasonOptions,
    refetchWithdrawalEligibility,
    submitRecaptchaToken,
  ]);

  const handleSubmitRecaptchaExpired = useCallback(() => {
    setSubmitRecaptchaToken(null);
    setSubmitRecaptchaKey(Date.now());
  }, []);

  const isSubmitting = submitInFlight;
  const isSubmitDisabled =
    isSubmitted ||
    isSubmitting ||
    isReturnConfirmOpen ||
    selectedCount === 0 ||
    isWithdrawalEligibilityLoading ||
    (isGuestMode && !submitRecaptchaToken);

  return {
    getLineEligibilityState,
    goBackToOrderDetails,
    handleRequestSubmit,
    handleSubmitRecaptchaExpired,
    isReturnConfirmOpen,
    isSubmitDisabled,
    isSubmitted,
    isSubmitting,
    lineReturnFields,
    performReturnSubmit,
    selectedLineIds,
    setIsReturnConfirmOpen,
    setLineSelected,
    setSubmitRecaptchaToken,
    submitErrorMessage,
    submitRecaptchaKey,
    updateLineField,
  };
}
